import React, { useCallback } from 'react';
import { BaseImportExportButtons } from './BaseImportExportButtons';
import { parseProcessorBulkExcel, generateProcessorTemplate } from '../../utils/processorBulkImportUtils';
import { exportProcessorsToExcel } from '../../utils/processorExcelUtils';
import { processorsAPI } from '../../services/api';

// Helper to extract municipality from processor address
function extractMunicipalityFromAddress(address) {
  if (!address || typeof address !== 'string') return null;
  const parts = address.split(',').map((p) => p.trim());
  if (parts.length >= 2) {
    const municipality = parts[parts.length - 2];
    if (municipality && municipality.length > 2) return municipality;
  }
  return null;
}

export function ProcessorImportExportButtons({ processors, onImport, userRole }) {
  const getMunicipality = useCallback((p) => {
    const direct = p.municipality || p.Municipality;
    if (direct && direct.trim() !== '') return direct;
    return extractMunicipalityFromAddress(p.address);
  }, []);

  const handleFileImport = useCallback(
    async (file) => {
      const parsed = await parseProcessorBulkExcel(file);
      if (!parsed || parsed.length === 0) {
        return { type: 'error', message: 'No valid processor data found in the file. Please check the format and try again.' };
      }

      const response = await processorsAPI.bulkImportComprehensive(parsed);
      if (response.success) {
        if (response.data.errorCount === 0) {
          onImport?.();
          return { type: 'success', message: `Successfully imported ${response.data.successCount} processors!` };
        }
        const errorSummary = response.data.errors
          .slice(0, 3)
          .map((err) => `Row ${err.row}: ${err.error}`)
          .join('; ');
        return {
          type: 'error',
          message: `Partially completed: ${response.data.successCount} successful, ${response.data.errorCount} failed. Errors: ${errorSummary}${response.data.errors.length > 3 ? '...' : ''}`,
        };
      }
      return { type: 'error', message: 'Import failed unexpectedly.' };
    },
    [onImport]
  );

  return (
    <BaseImportExportButtons
      entityName="processor"
      data={processors}
      userRole={userRole}
      getMunicipality={getMunicipality}
      onFileChange={handleFileImport}
      onExport={exportProcessorsToExcel}
      onDownloadTemplate={generateProcessorTemplate}
      accentColor="amber"
      infoText="Import complete processor data including business profile, processing details, post-harvest techniques, marketing info, and business insights. Download the template first to see the required format."
    />
  );
}
